<template>
  <div>
    <Button
      label="Add"
      fluid
      size="small"
      @click="dialog.visible = true"
    >
      <template #icon>
        <Icon :name="BaseIcon.ADD" />
      </template>
    </Button>

    <Dialog
      v-model:visible="dialog.visible"
      header="Add Relationship"
      modal
      :style="{ width: '35rem' }"
    >
      <div class="space-y-4">
        <BaseInputLabel
          label="Contact"
          :isRequired="true"
        >
          <ContactSelectSingle
            v-model:selectedContact="dialog.selectedContactId"
            :showCreateContact="false"
          />
        </BaseInputLabel>

        <BaseInputLabel
          label="Relationship"
          :isRequired="true"
        >
          <Select
            v-model="dialog.selectedRelationship"
            :options="relationships"
            optionLabel="label"
            optionValue="value"
            placeholder="Select relationship"
            fluid
          />
        </BaseInputLabel>

        <div class="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            label="Cancel"
            icon="pi pi-times"
            outlined
            :disabled="dialog.loading"
            @click="closeDialog"
          />
          <Button
            type="button"
            label="Save"
            icon="pi pi-check"
            :loading="dialog.loading"
            @click="addRelationship"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
import type { ContactRelationship } from '@prisma/client'
import { BaseIcon } from '~/types/baseIcon'

const route = useRoute()
const toast = useToast()
const { createContactLink } = useContacts()
const emit = defineEmits(['refresh'])

const contactId = route.params.contactId

const relationships = [
  {
    label: 'Business Partner',
    value: 'BUSINESS_PARTNER',
  },
  {
    label: 'Duplicate Profile',
    value: 'DUPLICATE_PROFILE',
  },
  {
    label: 'Family',
    value: 'FAMILY',
  },
  {
    label: 'Spouse',
    value: 'SPOUSE',
  },
]

const dialog = ref({
  visible: false,
  loading: false,
  selectedContactId: '',
  selectedRelationship: '' as ContactRelationship,
})

const closeDialog = () => {
  dialog.value.visible = false
  dialog.value.loading = false
  dialog.value.selectedContactId = ''
  dialog.value.selectedRelationship = '' as ContactRelationship
}

const addRelationship = async () => {
  dialog.value.loading = true

  try {
    // Check if required fields are filled
    if (!dialog.value.selectedContactId || !dialog.value.selectedRelationship) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill all required fields',
        life: 3000,
      })
      dialog.value.loading = false
      return
    }

    const contactLinkData = {
      contactId: contactId,
      contactIdToLink: dialog.value.selectedContactId,
      relationship: dialog.value.selectedRelationship,
    }

    if (contactLinkData.contactId === contactLinkData.contactIdToLink) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'A contact cannot be linked to itself. Please choose a different contact.',
        life: 3000,
      })
      dialog.value.loading = false
      return
    }

    const { data, error } = await createContactLink(contactLinkData)

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error creating contact relationship',
        life: 3000,
      })
      dialog.value.loading = false
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Contact relationship created successfully',
      life: 3000,
    })
    dialog.value.visible = false
    dialog.value.loading = false
    emit('refresh')
  }
  catch (error) {
    console.error('Error creating contact relationship:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create contact relationship',
      life: 3000,
    })
  }
  finally {
    dialog.value.visible = false
    dialog.value.loading = false
  }
}
</script>
